import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { useAuthStore } from './lib/auth';
import './App.css';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import CompleteProfile from './pages/CompleteProfile';
import Dashboard from './pages/Dashboard';
import BoardsList from './pages/BoardsList';
import BoardCreate from './pages/BoardCreate';
import BoardStats from './pages/BoardStats';
import BoardEdit from './pages/BoardEdit';
import Community from './pages/Community';
import CommunityBoard from './pages/CommunityBoard';
import CreatorProfile from './pages/CreatorProfile';
import Sprint from './pages/Sprint';
import SprintPlay from './pages/SprintPlay';
import SprintResults from './pages/SprintResults';
import SprintStats from './pages/SprintStats';
import ProfileSettings from './pages/ProfileSettings';
import Achievements from './pages/Achievements';
import DailyChallenge from './pages/DailyChallenge';
import DailyChallengePlay from './pages/DailyChallengePlay';
import StudyPath from './pages/StudyPath';
import StudyPathPlay from './pages/StudyPathPlay';
import GameLobby from './pages/GameLobby';
import GamePlay from './pages/GamePlay';
import GameJoin from './pages/GameJoin';
import AskAlex from './pages/AskAlex';
import AskAlexMission from './pages/AskAlexMission';
import BeatTheData from './pages/BeatTheData';
import KnowledgePortfolio from './pages/KnowledgePortfolio';
import ClueExplorer from './pages/ClueExplorer';
import FlashCards from './pages/FlashCards';
import DailyCategories from './pages/DailyCategories';
import DailyCategoriesStats from './pages/DailyCategoriesStats';
import BoardPlay from './pages/BoardPlay';
import AdminPanel from './pages/AdminPanel';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Pricing from './pages/Pricing';
import Contact from './pages/Contact';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isInitializing } = useAuthStore();
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[hsl(var(--brand-gold-500))]"></div>
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Detect OAuth callback from URL hash before rendering routes
function AppRouter() {
  const location = useLocation();
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Check URL fragment for session_id synchronously (before ProtectedRoute runs)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/complete-profile" element={<CompleteProfile />} />
      <Route path="/join" element={<GameJoin />} />
      <Route path="/join/:pin" element={<GameJoin />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
          <Route path="/boards" element={<ProtectedRoute><BoardsList /></ProtectedRoute>} />
          <Route path="/boards/new" element={<ProtectedRoute><BoardCreate /></ProtectedRoute>} />
          <Route path="/boards/stats" element={<ProtectedRoute><BoardStats /></ProtectedRoute>} />
          <Route path="/boards/:boardId/edit" element={<ProtectedRoute><BoardEdit /></ProtectedRoute>} />
          <Route path="/boards/:boardId/play" element={<ProtectedRoute><BoardPlay /></ProtectedRoute>} />
          
          {/* Community */}
          <Route path="/community" element={<Community />} />
          <Route path="/community/:boardId" element={<CommunityBoard />} />
          <Route path="/community/:boardId/play" element={<ProtectedRoute><BoardPlay /></ProtectedRoute>} />
          <Route path="/creator/:userId" element={<CreatorProfile />} />
          
          {/* Admin */}
          <Route path="/admin" element={<AdminPanel />} />

          {/* Legal */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Blog */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Pricing */}
          <Route path="/pricing" element={<Pricing />} />

          {/* Contact */}
          <Route path="/contact" element={<Contact />} />
          
          {/* Sprint Mode */}
          <Route path="/sprint" element={<ProtectedRoute><Sprint /></ProtectedRoute>} />
          <Route path="/sprint/stats" element={<ProtectedRoute><SprintStats /></ProtectedRoute>} />
          <Route path="/sprint/:sessionId" element={<ProtectedRoute><SprintPlay /></ProtectedRoute>} />
          <Route path="/sprint/:sessionId/results" element={<ProtectedRoute><SprintResults /></ProtectedRoute>} />
          
          {/* AskAlex Mode */}
          <Route path="/askalex" element={<ProtectedRoute><AskAlex /></ProtectedRoute>} />
          <Route path="/askalex/mission/:missionId" element={<ProtectedRoute><AskAlexMission /></ProtectedRoute>} />
          <Route path="/askalex/beatthedata" element={<ProtectedRoute><BeatTheData /></ProtectedRoute>} />
          <Route path="/askalex/portfolio" element={<ProtectedRoute><KnowledgePortfolio /></ProtectedRoute>} />
          <Route path="/askalex/explore" element={<ProtectedRoute><ClueExplorer /></ProtectedRoute>} />
          <Route path="/askalex/flashcards" element={<ProtectedRoute><FlashCards /></ProtectedRoute>} />
          
          {/* Daily Challenge */}
          <Route path="/daily-challenge" element={<ProtectedRoute><DailyChallenge /></ProtectedRoute>} />
          <Route path="/daily-challenge/play/:submissionId" element={<ProtectedRoute><DailyChallengePlay /></ProtectedRoute>} />
          
          {/* Daily Categories */}
          <Route path="/daily-categories" element={<ProtectedRoute><DailyCategories /></ProtectedRoute>} />
          <Route path="/daily-categories/stats" element={<ProtectedRoute><DailyCategoriesStats /></ProtectedRoute>} />
          
          {/* Study Path */}
          <Route path="/study-path" element={<ProtectedRoute><StudyPath /></ProtectedRoute>} />
          <Route path="/study-path/play/:sessionId" element={<ProtectedRoute><StudyPathPlay /></ProtectedRoute>} />
          
          {/* Game */}
          <Route path="/game/lobby/:sessionId" element={<ProtectedRoute><GameLobby /></ProtectedRoute>} />
          <Route path="/game/:sessionId" element={<GamePlay />} />
          
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function App() {
  const initAuth = useAuthStore((state) => state.initAuth);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  return (
    <Router>
      <div className="min-h-screen bg-[hsl(var(--background))] overflow-x-hidden">
        <AppRouter />
        
        <Toaster 
          position="bottom-center"
          toastOptions={{
            style: {
              background: 'hsl(230 20% 12%)',
              border: '1px solid hsl(230 22% 22%)',
              color: 'hsl(0 0% 98%)',
            },
          }}
        />
      </div>
    </Router>
  );
}

export default App;
